import * as React from 'react';
import {
  Config,
  Decorator,
  Mutator,
  FormState,
  FormSubscription,
} from 'final-form';
import {
  FormRenderProps as FinalFormFormRenderProps,
  Form as FinalFormForm,
} from 'react-final-form';
import {ValidationException} from '@practicegenius/http-clients-js/src/exceptions';
import InitializeInitialErrors, {
  setSessionErrors,
  ErrorList,
} from './effects/handlesInitialErrors';

export type FormRenderProps = FinalFormFormRenderProps & {
  submitDisabled: boolean;
  combinedErrors: any;
  initialErrors: string[] | undefined | null;
};

type FormProps = Config & {
  initialValues?: Record<string, any>;
  subscription?: FormSubscription;
  decorators?: Decorator[];
  mutators?: {
    [x: string]: Mutator;
  };
  initialErrors?: ErrorList;
  children: React.ReactNode | ((a: FormRenderProps) => React.ReactNode);
};

/**
 * Given FormRenderProps should we disable submit?
 */
function formRPToDisableSubmit({
  pristine,
  invalid,
  dirtySinceLastSubmit,
  submitting,
}: {
  pristine?: boolean;
  invalid?: boolean;
  submitting?: boolean;
  dirtySinceLastSubmit?: boolean;
}): boolean {
  return Boolean(
    (pristine && invalid) || submitting || (invalid && !dirtySinceLastSubmit),
  );
}

/**
 * Produce relevant form errors considering formState
 */
function formStateToCombinedErrors(formState: FormState): {
  [name: string]: string;
} {
  return (
    Object.keys({
      ...formState.errors,
      ...formState.submitErrors,
    })
      .map(name => ({
        name,
        message:
          // Give precedence to client-side errors.
          (formState.errors && formState.errors[name]) ||
          // Display server-side errors only when appropriate.
          (!formState.dirtySinceLastSubmit &&
            !formState.submitting &&
            formState.submitErrors &&
            formState.submitErrors[name]),
      }))
      .filter(({message}) => Boolean(message))
      // Turn it into our "errors, keyed by field name" object
      .reduce((acc, {name, message}) => ({...acc, [name]: message}), {})
  );
}

/**
 * Our own, slightly enhanced Form component
 */
export function Form({
  children,
  onSubmit,
  initialErrors,
  mutators = {},
  ...otherProps
}: FormProps) {
  const handleSubmit: typeof onSubmit = React.useCallback(
    async (...args) => {
      try {
        // @ts-ignore
        await onSubmit(...args);
        // Ensure that submitErrors are not accidentally set by a request resolving with an object such as an updated entity
        // submitErrors must be set via throwing ValidationError
        return {};
      } catch (error) {
        // props.onSubmit may throw ValidationError
        // Pipe this through to react-final-form submitErrors
        if (error instanceof ValidationException) {
          return error.errors;
        }
        throw error;
      }
    },
    [onSubmit],
  );

  return (
    <FinalFormForm
      {...otherProps}
      onSubmit={handleSubmit}
      mutators={{...mutators, setSessionErrors}}
    >
      {formRP => (
        <form onSubmit={formRP.handleSubmit} style={{margin: 0}} noValidate>
          {typeof children === 'function'
            ? children({
                ...formRP,
                submitDisabled: formRPToDisableSubmit(formRP),
                combinedErrors: formStateToCombinedErrors(
                  formRP.form.getState(),
                ),
                // @ts-ignore
                initialErrors: initialErrors
                  ? Object.keys(initialErrors || {}).reduce(
                      // @ts-ignore
                      (acc, key) => [...acc, initialErrors[key]],
                      [],
                    )
                  : null,
              })
            : children}
          <InitializeInitialErrors initialErrors={initialErrors} />
        </form>
      )}
    </FinalFormForm>
  );
}
